/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import {
    CForm, CRow, CCol, CFormLabel, CFormInput, CFormTextarea,
    CFormSelect, CButton, CSpinner, CAlert,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CBadge,
    CCard, CCardBody, CNav, CNavItem, CNavLink,
    CTabContent, CTabPane, CTable, CTableHead, CTableRow,
    CTableHeaderCell, CTableBody, CTableDataCell, CInputGroup
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX, cilImage, cilPlus, cilMinus, cilColorPalette, cilGrid } from '@coreui/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { getColorRequestAction } from '../../store/color/colorActions';
import {
    createComposition,
    createWeaveType,
    getSampleColorDropdown,
    getSampleColors,
    getSampleFactors,
    getWeaveDropdown
} from '../../services/endpoints';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import Cropper from 'react-easy-crop';
import './SampleInputs.scss';

const SampleInputs = ({
    onSubmit,
    onCancel,
    isLoading,
    error,
    initialData,
    compositionOptions = [],
    weaveTypeOptions = [],
    currencyOptions = [],
    dropdownLoading,
    dropdownError,
    onDropdownRefresh
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        CardNo: '',
        CompanyName: '',
        Note: '',
        Certificate: '',
        FabricWidth: '',
        FabricWeight: '',
        ReedWidth: '',
        PriceWhole: '',
        PriceDecimal: '',
        Datetime: new Date().toISOString().split('T')[0], // Default to today's date
        CompositionGuid: '',
        WeaveTypeGuid: '',
        CurrencyGuid: '',
        Photo: '',
        TotalWireCount: '',
        ReedNo: '',
        Dgts: '',
        WarpDensityWhole: '',
        WarpDensityDecimal: '',
        WeftDensityWhole: '',
        WeftDensityDecimal: '',
        GrayWarpWhole: '',
        GrayWarpDecimal: '',
        GrayWeftWhole: '',
        GrayWeftDecimal: ''
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [imageModal, setImageModal] = useState(false);

    // Crop modal states
    const [cropModalVisible, setCropModalVisible] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Table states for WARP/WEFT (like WeaveInputs)
    const [showColorTable, setShowColorTable] = useState(false);
    const [activeTab, setActiveTab] = useState('cozgu');
    const [warpTableData, setWarpTableData] = useState([]);
    const [weftTableData, setWeftTableData] = useState([]);
    const [warpTableColumns, setWarpTableColumns] = useState(1);
    const [weftTableColumns, setWeftTableColumns] = useState(1);
    const [bulkAddRows, setBulkAddRows] = useState({ warp: 1, weft: 1 });
    const [bulkAddColumns, setBulkAddColumns] = useState({ warp: 1, weft: 1 });
    const [loadingSampleColors, setLoadingSampleColors] = useState(false);
    const [showSampleFactorModal, setShowSampleFactorModal] = useState(false);
    const [sampleFactorModalLoading, setSampleFactorModalLoading] = useState(false);
    const [sampleFactorModalError, setSampleFactorModalError] = useState('');
    const [sampleFactorRows, setSampleFactorRows] = useState([]);
    const [loadedSampleFactorRowsByThreadType, setLoadedSampleFactorRowsByThreadType] = useState({
        1: [],
        2: []
    });
    const [pendingSampleFactorRowsByThreadType, setPendingSampleFactorRowsByThreadType] = useState({
        1: null,
        2: null
    });

    // Dropdown data for color/yarn selection
    const [dropdownColors, setDropdownColors] = useState([]);
    const [dropdownYarns, setDropdownYarns] = useState([]);
    const [loadingDropdownData, setLoadingDropdownData] = useState(false);
    const [showAddCompositionModal, setShowAddCompositionModal] = useState(false);
    const [showAddWeaveTypeModal, setShowAddWeaveTypeModal] = useState(false);
    const [addCompositionLoading, setAddCompositionLoading] = useState(false);
    const [addWeaveTypeLoading, setAddWeaveTypeLoading] = useState(false);
    const [addCompositionForm, setAddCompositionForm] = useState({ threadName: '', threadDescription: '' });
    const [addWeaveTypeForm, setAddWeaveTypeForm] = useState({ weaveTypeName: '', weaveTypeDescription: '' });
    const [addCompositionErrors, setAddCompositionErrors] = useState({});
    const [addWeaveTypeErrors, setAddWeaveTypeErrors] = useState({});
    const [quickCreateFeedback, setQuickCreateFeedback] = useState(null);

    const colorList = useSelector(
        state => state.color?.colorList || state.colorReducer?.colorList || []
    );

    const getCurrentThreadType = () => (activeTab === 'cozgu' ? 2 : 1);

    const getCurrentSampleGuid = () => (
        initialData?.SampleId || initialData?.PrdSampleGuid || initialData?.SampleGuid || null
    );

    const createEmptySampleFactorRow = (threadType) => ({
        localId: `${Date.now()}-${Math.random()}`,
        sampleFactorId: '',
        threadType,
        firstColon: '',
        secondColon: '',
        factor: ''
    });

    const normalizeSampleFactorRowsFromApi = (items, threadType) => {
        if (!Array.isArray(items)) return [createEmptySampleFactorRow(threadType)];

        const filtered = items.filter(item => Number(item?.ThreadType) === Number(threadType));
        if (filtered.length === 0) return [createEmptySampleFactorRow(threadType)];

        return filtered.map((item, idx) => ({
            localId: `${Date.now()}-${idx}-${Math.random()}`,
            sampleFactorId: item?.SampleFactorId || '',
            threadType,
            firstColon: item?.FirstColon ?? '',
            secondColon: item?.SecondColon ?? '',
            factor: item?.Factor ?? ''
        }));
    };

    const getEffectiveSampleFactorRowsByThreadType = (threadType) => {
        const pendingRows = pendingSampleFactorRowsByThreadType?.[threadType];
        if (Array.isArray(pendingRows)) return pendingRows;
        return loadedSampleFactorRowsByThreadType?.[threadType] || [];
    };

    const getFactorRowsForThreadType = (threadType, includeColors = false) => {
        const rows = getEffectiveSampleFactorRowsByThreadType(threadType);
        const colorPalette = ['#fff59d', '#c8e6c9'];

        return rows
            .map((row, idx) => {
                const start = Number(row?.firstColon);
                const end = Number(row?.secondColon);
                const factor = Number(row?.factor);

                if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(factor)) return null;
                if (start <= 0 || end <= 0 || start > end || factor <= 0) return null;

                const parsedRow = { start, end, factor };
                if (includeColors) {
                    parsedRow.backgroundColor = colorPalette[idx % colorPalette.length];
                }
                return parsedRow;
            })
            .filter(Boolean);
    };

    const getColumnFactorForThreadType = (threadType, columnNo) => {
        const factorRows = getFactorRowsForThreadType(threadType, false);
        const matchedRange = factorRows.find(range => columnNo >= range.start && columnNo <= range.end);
        return matchedRange?.factor || 1;
    };

    const getWeightedRowTotalForThreadType = (row, threadType) => {
        if (!row?.values?.length) return 0;
        return row.values.reduce((sum, value, colIndex) => {
            const threadCount = parseInt(value, 10) || 0;
            const factor = getColumnFactorForThreadType(threadType, colIndex + 1);
            return sum + (threadCount * factor);
        }, 0);
    };

    const parseNumeric = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number(String(value).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
    };

    const round4AwayFromZero = (value) => {
        if (!Number.isFinite(value)) return 0;

        const precision = 10000;
        if (value >= 0) {
            return Math.round((value + Number.EPSILON) * precision) / precision;
        }

        return -Math.round((Math.abs(value) + Number.EPSILON) * precision) / precision;
    };

    const scaleWeightToInt = (rawWeight) => {
        const rounded = round4AwayFromZero(rawWeight);
        const scaled = Math.round(rounded * 10000);

        if (scaled > 2147483647) return 2147483647;
        if (scaled < -2147483648) return -2147483648;

        return scaled;
    };

    const getYarnInfo = (yarnId) => dropdownYarns.find(yarn => yarn.Guid === yarnId);

    const calculateBaseWeightByThreadType = (threadType, yarnFactor) => {
        const reedWidth = parseNumeric(formData.ReedWidth);
        const normalizedYarnFactor = parseNumeric(yarnFactor);

        if (!reedWidth || !normalizedYarnFactor) return null;

        if (threadType === 1) {
            const grayWeftWhole = parseNumeric(formData.GrayWeftWhole) || 0;
            const grayWeftDecimal = parseNumeric(formData.GrayWeftDecimal) || 0;
            const grayWeft = (grayWeftWhole * 100 + grayWeftDecimal) / 100;
            if (grayWeft <= 0) return null;

            return (grayWeft * 0.65 / (normalizedYarnFactor / 100)) * reedWidth;
        }

        if (threadType === 2) {
            const reedNo = parseNumeric(formData.ReedNo);
            const dgts = parseNumeric(formData.Dgts);
            if (!reedNo || !dgts) return null;

            return ((((reedNo * dgts) * 0.65 / (normalizedYarnFactor / 100)) * reedWidth) / 10);
        }

        return null;
    };

    const calculateTableWeightsForThreadType = (tableData, threadType) => {
        if (!Array.isArray(tableData) || tableData.length === 0) return tableData;

        const rowTotals = tableData.map(row => getWeightedRowTotalForThreadType(row, threadType));
        const grandTotal = rowTotals.reduce((sum, total) => sum + total, 0);

        return tableData.map((row, index) => {
            const rowTotal = rowTotals[index];
            const yarnFactor = parseNumeric(getYarnInfo(row.yarnId)?.YarnFactor);
            const baseWeight = calculateBaseWeightByThreadType(threadType, yarnFactor);

            if (!baseWeight || grandTotal <= 0 || rowTotal <= 0) {
                return { ...row, weight: '' };
            }

            const rowRatio = rowTotal / grandTotal;
            const weightedGram = baseWeight * rowRatio;

            return { ...row, weight: scaleWeightToInt(weightedGram) };
        });
    };

    const loadSampleFactorsForReport = async (sampleGuid) => {
        if (!sampleGuid) {
            setLoadedSampleFactorRowsByThreadType({ 1: [], 2: [] });
            return;
        }

        try {
            const sessionUserId = localStorage.getItem('sessionUserId');
            const { data } = await getSampleFactors({
                SessionUserId: sessionUserId,
                SampleId: String(sampleGuid),
                PrdSampleGuid: String(sampleGuid)
            });

            const list = data?.SampleFactorList || [];
            setLoadedSampleFactorRowsByThreadType({
                1: normalizeSampleFactorRowsFromApi(list, 1),
                2: normalizeSampleFactorRowsFromApi(list, 2)
            });
        } catch (factorError) {
            console.error('Error loading sample factor report data:', factorError);
            setLoadedSampleFactorRowsByThreadType({ 1: [], 2: [] });
        }
    };

    const handleOpenSampleFactorModal = async () => {
        const threadType = getCurrentThreadType();
        setShowSampleFactorModal(true);
        setSampleFactorModalError('');
        setSampleFactorModalLoading(true);

        try {
            const sessionUserId = localStorage.getItem('sessionUserId');
            const sampleGuid = getCurrentSampleGuid();
            let rowsFromApi = [createEmptySampleFactorRow(threadType)];

            if (sampleGuid) {
                const { data } = await getSampleFactors({
                    SessionUserId: sessionUserId,
                    SampleId: String(sampleGuid),
                    PrdSampleGuid: String(sampleGuid)
                });
                rowsFromApi = normalizeSampleFactorRowsFromApi(data?.SampleFactorList || [], threadType);
            }

            setLoadedSampleFactorRowsByThreadType(prev => ({
                ...prev,
                [threadType]: rowsFromApi
            }));

            const pendingRows = pendingSampleFactorRowsByThreadType?.[threadType];
            if (Array.isArray(pendingRows)) {
                setSampleFactorRows(
                    pendingRows.length > 0 ? pendingRows : [createEmptySampleFactorRow(threadType)]
                );
            } else {
                setSampleFactorRows(rowsFromApi);
            }
        } catch (factorError) {
            console.error('Error loading sample factors:', factorError);
            setSampleFactorRows([createEmptySampleFactorRow(threadType)]);
            setSampleFactorModalError(t('weaveFactorLoadError') || 'Carpan verileri yuklenemedi.');
        } finally {
            setSampleFactorModalLoading(false);
        }
    };

    const handleCloseSampleFactorModal = () => {
        setShowSampleFactorModal(false);
        setSampleFactorModalError('');
    };

    const handleAddSampleFactorRow = () => {
        const threadType = getCurrentThreadType();
        setSampleFactorRows(prev => [...prev, createEmptySampleFactorRow(threadType)]);
    };

    const handleRemoveSampleFactorRow = (localId) => {
        setSampleFactorRows(prev => {
            const next = prev.filter(row => row.localId !== localId);
            return next.length > 0 ? next : [createEmptySampleFactorRow(getCurrentThreadType())];
        });
    };

    const hasSampleFactorFirstSecondConflict = (rows) => {
        const normalizedRows = rows.map((row, idx) => ({
            idx,
            firstColon: String(row.firstColon || '').trim(),
            secondColon: String(row.secondColon || '').trim()
        }));

        return normalizedRows.some((row) => {
            if (!row.firstColon) return false;

            return normalizedRows.some(
                otherRow => otherRow.idx !== row.idx && otherRow.secondColon === row.firstColon
            );
        });
    };

    const handleSampleFactorInputChange = (localId, field, value) => {
        const cleanValue = value === '' ? '' : value.replace(/[^0-9]/g, '');

        setSampleFactorRows(prev => {
            const nextRows = prev.map(row => (
                row.localId === localId ? { ...row, [field]: cleanValue } : row
            ));

            if (hasSampleFactorFirstSecondConflict(nextRows)) {
                setSampleFactorModalError('Ikinci sutundaki deger ilk sutunda kullanilamaz.');
                return nextRows;
            }

            setSampleFactorModalError('');
            return nextRows;
        });
    };

    const handleSaveSampleFactorModal = () => {
        const threadType = getCurrentThreadType();

        // Filter out completely empty placeholder rows (created when last row is deleted)
        const nonEmptyRows = sampleFactorRows.filter(
            row => String(row.firstColon || '').trim() ||
                String(row.secondColon || '').trim() ||
                String(row.factor || '').trim()
        );

        // Validate only non-empty rows: each must have all three fields filled
        const hasInvalidRow = nonEmptyRows.some(
            row => !(String(row.firstColon || '').trim() &&
                String(row.secondColon || '').trim() &&
                String(row.factor || '').trim()),
        );

        if (hasInvalidRow) {
            setSampleFactorModalError(
                t('Sütun ve çarpan bilgilerini doldurunuz') ||
                'Lutfen tum satirlarda ilk sutun, ikinci sutun ve carpan alanlarini doldurun.'
            );
            return;
        }

        if (hasSampleFactorFirstSecondConflict(nonEmptyRows)) {
            setSampleFactorModalError('Ikinci sutundaki deger ilk sutunda kullanilamaz.');
            return;
        }

        const cleanedRows = nonEmptyRows.map(row => ({
            ...row,
            threadType,
            firstColon: String(row.firstColon).trim(),
            secondColon: String(row.secondColon).trim(),
            factor: String(row.factor).trim()
        }));

        setPendingSampleFactorRowsByThreadType(prev => ({
            ...prev,
            [threadType]: cleanedRows
        }));
        setShowSampleFactorModal(false);
        setSampleFactorModalError('');
    };

    const resetAddCompositionModal = () => {
        setAddCompositionForm({ threadName: '', threadDescription: '' });
        setAddCompositionErrors({});
    };

    const resetAddWeaveTypeModal = () => {
        setAddWeaveTypeForm({ weaveTypeName: '', weaveTypeDescription: '' });
        setAddWeaveTypeErrors({});
    };

    const handleOpenAddCompositionModal = () => {
        resetAddCompositionModal();
        setShowAddCompositionModal(true);
    };

    const handleOpenAddWeaveTypeModal = () => {
        resetAddWeaveTypeModal();
        setShowAddWeaveTypeModal(true);
    };

    const refreshDropdownOptions = async (filters = {}) => {
        if (typeof onDropdownRefresh === 'function') {
            return await onDropdownRefresh(filters);
        }
        return null;
    };

    const handleDropdownSearch = async (fieldName, searchTerm) => {
        if (typeof onDropdownRefresh !== 'function') return;

        const trimmedSearchTerm = searchTerm?.trim() || '';
        if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < 3) return;

        if (fieldName === 'CompositionGuid') {
            await refreshDropdownOptions({ CompositionSearchTerm: trimmedSearchTerm });
            return;
        }

        if (fieldName === 'WeaveTypeGuid') {
            await refreshDropdownOptions({ WeaveTypeSearchTerm: trimmedSearchTerm });
        }
    };

    const handleAddCompositionSubmit = async () => {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const newErrors = {};

        if (!addCompositionForm.threadName.trim()) {
            newErrors.threadName = t('threadNameRequired') || 'Kompozisyon adi zorunlu.';
        }
        if (!sessionUserId) {
            newErrors.general = t('userSessionNotFound') || 'Kullanici oturumu bulunamadi.';
        }

        setAddCompositionErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setAddCompositionLoading(true);
        try {
            const createdName = addCompositionForm.threadName.trim();
            const response = await createComposition({
                SessionUserId: sessionUserId,
                ThreadName: createdName,
                ThreadDescription: addCompositionForm.threadDescription.trim(),
            });
            const data = response?.data || response;

            if (data?.Result?.ErrorCode === 0) {
                const nextDropdownData = await refreshDropdownOptions();
                const createdComposition = (nextDropdownData?.Compositions || compositionOptions || []).find(
                    item => (item.ThreadName || item.CompositionName || item.Name) === createdName
                );
                const createdGuid = createdComposition?.CompositionGuid || createdComposition?.PrdCompositionGuid;

                if (createdGuid) {
                    setFormData(prev => ({ ...prev, CompositionGuid: createdGuid }));
                }
                setQuickCreateFeedback({
                    type: 'success',
                    message: data?.Result?.ErrorDescription || (t('compositionCreated') || 'Kompozisyon olusturuldu.')
                });
                setShowAddCompositionModal(false);
                resetAddCompositionModal();
            } else {
                setAddCompositionErrors({
                    general: data?.Result?.ErrorDescription || (t('anErrorOccurred') || 'Kayit sirasinda hata olustu.')
                });
            }
        } catch (err) {
            setAddCompositionErrors({
                general: err?.response?.data?.Result?.ErrorDescription || (t('anErrorOccurred') || 'Kayit sirasinda hata olustu.')
            });
        } finally {
            setAddCompositionLoading(false);
        }
    };

    const handleAddWeaveTypeSubmit = async () => {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const newErrors = {};

        if (!addWeaveTypeForm.weaveTypeName.trim()) {
            newErrors.weaveTypeName = t('weaveTypeNameRequired') || 'Orgu tipi adi zorunlu.';
        }
        if (!sessionUserId) {
            newErrors.general = t('userSessionNotFound') || 'Kullanici oturumu bulunamadi.';
        }

        setAddWeaveTypeErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setAddWeaveTypeLoading(true);
        try {
            const createdName = addWeaveTypeForm.weaveTypeName.trim();
            const response = await createWeaveType({
                SessionUserId: sessionUserId,
                WeaveTypeName: createdName,
                WeaveTypeDescription: addWeaveTypeForm.weaveTypeDescription.trim(),
            });
            const data = response?.data || response;

            if (data?.Result?.ErrorCode === 0) {
                const nextDropdownData = await refreshDropdownOptions();
                const createdWeaveType = (nextDropdownData?.WeaveTypes || weaveTypeOptions || []).find(
                    item => (item.WeaveTypeName || item.Name) === createdName
                );
                const createdGuid = createdWeaveType?.WeaveTypeGuid || createdWeaveType?.PrdWeaveTypeGuid;

                if (createdGuid) {
                    setFormData(prev => ({ ...prev, WeaveTypeGuid: createdGuid }));
                }
                setQuickCreateFeedback({
                    type: 'success',
                    message: data?.Result?.ErrorDescription || (t('weaveTypeCreated') || 'Orgu tipi olusturuldu.')
                });
                setShowAddWeaveTypeModal(false);
                resetAddWeaveTypeModal();
            } else {
                setAddWeaveTypeErrors({
                    general: data?.Result?.ErrorDescription || (t('anErrorOccurred') || 'Kayit sirasinda hata olustu.')
                });
            }
        } catch (err) {
            setAddWeaveTypeErrors({
                general: err?.response?.data?.Result?.ErrorDescription || (t('anErrorOccurred') || 'Kayit sirasinda hata olustu.')
            });
        } finally {
            setAddWeaveTypeLoading(false);
        }
    };

    // Load color list on mount
    useEffect(() => {
        dispatch(getColorRequestAction({ SessionUserId: localStorage.getItem('sessionUserId') }));
    }, [dispatch]);

        // Load SampleColor dropdown data
    useEffect(() => {
        const loadDropdownData = async () => {
            setLoadingDropdownData(true);
            try {
                const sessionUserId = localStorage.getItem('sessionUserId');
                const [{ data: sampleDropdownData }, { data: weaveDropdownData }] = await Promise.all([
                    getSampleColorDropdown({ SessionUserId: sessionUserId }),
                    getWeaveDropdown({ SessionUserId: sessionUserId })
                ]);

                if (sampleDropdownData?.Colors) {
                    const colors = sampleDropdownData.Colors.map(c => ({
                        ColorId: c.PrdColorGuid,
                        ColorName: c.ColorName,
                        ColorCode: c.ColorCode || ''
                    }));
                    setDropdownColors(colors);
                }

                if (sampleDropdownData?.Yarns) {
                    const yarnFactorMap = new Map(
                        (weaveDropdownData?.Yarns || []).map((item) => {
                            const guid = String(item?.PrdYarnGuid || item?.YarnId || item?.Guid || '').trim();
                            return [guid, parseNumeric(item?.YarnFactor)];
                        })
                    );

                    setDropdownYarns(sampleDropdownData.Yarns.map(y => {
                        const guid = String(y.PrdYarnGuid || '').trim();
                        return {
                            Guid: y.PrdYarnGuid,
                            Name: y.YarnCode ? `${y.YarnCode} - ${y.YarnName}` : y.YarnName,
                            YarnCode: y.YarnCode,
                            YarnFactor: yarnFactorMap.get(guid) ?? null
                        };
                    }));
                }
            } catch (error) {
                console.error('Error loading SampleColor dropdown:', error);
            } finally {
                setLoadingDropdownData(false);
            }
        };
        loadDropdownData();
    }, []);

    // Update table data when dropdown colors are loaded (to fill colorCode)
    useEffect(() => {
        if (dropdownColors.length > 0) {
            // Update warp table with color codes
            setWarpTableData(prev => prev.map(row => {
                if (row.colorId && !row.colorCode) {
                    const colorInfo = dropdownColors.find(c => c.ColorId === row.colorId);
                    if (colorInfo) {
                        return {
                            ...row,
                            colorName: colorInfo.ColorName || row.colorName,
                            colorCode: colorInfo.ColorCode || row.colorCode
                        };
                    }
                }
                return row;
            }));

            // Update weft table with color codes
            setWeftTableData(prev => prev.map(row => {
                if (row.colorId && !row.colorCode) {
                    const colorInfo = dropdownColors.find(c => c.ColorId === row.colorId);
                    if (colorInfo) {
                        return {
                            ...row,
                            colorName: colorInfo.ColorName || row.colorName,
                            colorCode: colorInfo.ColorCode || row.colorCode
                        };
                    }
                }
                return row;
            }));
        }
    }, [dropdownColors]);

    // Update table data when dropdown yarns are loaded (to fill yarnName)
    useEffect(() => {
        if (dropdownYarns.length > 0) {
            // Update warp table with yarn names
            setWarpTableData(prev => prev.map(row => {
                if (row.yarnId && !row.yarnName) {
                    const yarnInfo = dropdownYarns.find(y => y.Guid === row.yarnId);
                    if (yarnInfo) {
                        return { ...row, yarnName: yarnInfo.Name || row.yarnName };
                    }
                }
                return row;
            }));

            // Update weft table with yarn names
            setWeftTableData(prev => prev.map(row => {
                if (row.yarnId && !row.yarnName) {
                    const yarnInfo = dropdownYarns.find(y => y.Guid === row.yarnId);
                    if (yarnInfo) {
                        return { ...row, yarnName: yarnInfo.Name || row.yarnName };
                    }
                }
                return row;
            }));
        }
    }, [dropdownYarns]);

    // Load SampleColor data when editing
    const loadSampleColors = async (sampleGuid) => {
        if (!sampleGuid) {
            console.log('No sampleGuid provided for loading SampleColors');
            return;
        }

        console.log('Loading SampleColors for PrdSampleGuid:', sampleGuid);
        setLoadingSampleColors(true);
        try {
            const sessionUserId = localStorage.getItem('sessionUserId');
            const payload = {
                SessionUserId: sessionUserId,
                PrdSampleGuid: sampleGuid.toString()
            };
            console.log('GetSampleColors request payload:', payload);

            const response = await getSampleColors(payload);
            console.log('GetSampleColors response:', response);

            const data = response?.data;
            if (data?.SampleColorList && data.SampleColorList.length > 0) {
                console.log('Found SampleColors:', data.SampleColorList);
                transformSampleColorToTable(data.SampleColorList);
            } else {
                console.log('No SampleColors found for this sample');
            }
        } catch (error) {
            console.error('Error loading sample colors:', error);
        } finally {
            setLoadingSampleColors(false);
        }
    };

    // Transform SampleColor API data to table format
    const transformSampleColorToTable = (sampleColorList) => {
        const warpData = sampleColorList.filter(item => item.ThreadType === 2);
        const weftData = sampleColorList.filter(item => item.ThreadType === 1);

        if (warpData.length > 0) {
            const warpGroups = processTableData(warpData, 2);
            setWarpTableData(warpGroups.tableRows);
            setWarpTableColumns(warpGroups.columnCount);
        }

        if (weftData.length > 0) {
            const weftGroups = processTableData(weftData, 1);
            setWeftTableData(weftGroups.tableRows);
            setWeftTableColumns(weftGroups.columnCount);
        }

        if (weftData.length > 0 && warpData.length === 0) {
            setActiveTab('atki');
        } else {
            setActiveTab('cozgu');
        }
    };

    const processTableData = (dataList, threadType) => {
        if (!dataList || dataList.length === 0) {
            return { tableRows: [], columnCount: 1 };
        }

        const tableRowsByKey = new Map();
        let maxPosition = 0;

        dataList.forEach(item => {
            maxPosition = Math.max(maxPosition, parseInt(item.Position) || 0);
        });

        const columnCount = Math.max(1, maxPosition);

        dataList.forEach((item, index) => {
            const position = parseInt(item.Position) || 1;
            const threadCount = item.ThreadCount || 0;
            const calculatedWeight = threadType === 1 ? (item.WeftWeight || 0) : (item.WaftWeight || 0);

            // Find color and yarn info from dropdowns
            const colorInfo = dropdownColors.find(c => c.ColorId === item.PrdColorGuid);
            const yarnInfo = dropdownYarns.find(y => y.Guid === item.PrdYarnGuid);

            const rowKey = `${item.PrdColorGuid || ''}-${item.PrdYarnGuid || ''}`;

            if (!tableRowsByKey.has(rowKey)) {
                tableRowsByKey.set(rowKey, {
                    id: Date.now() + index + Math.random(),
                    colorId: item.PrdColorGuid || '',
                    yarnId: item.PrdYarnGuid || '',
                    colorName: colorInfo?.ColorName || item.ColorName || '',
                    colorCode: colorInfo?.ColorCode || item.ColorCode || '',
                    yarnName: yarnInfo?.Name || item.YarnName || '',
                    weight: calculatedWeight || '',
                    sampleRowSorting: parseInt(item.SampleRowSorting, 10) || index + 1,
                    values: new Array(columnCount).fill(''),
                    sampleColorId: item.SampleColorId
                });
            }

            const existingRow = tableRowsByKey.get(rowKey);
            if (position > 0 && position <= columnCount) {
                existingRow.values[position - 1] = threadCount.toString();
            }

            if (!existingRow.sampleColorId && item.SampleColorId) {
                existingRow.sampleColorId = item.SampleColorId;
            }

            if (!existingRow.weight && calculatedWeight) {
                existingRow.weight = calculatedWeight;
            }

            const sampleRowSorting = parseInt(item.SampleRowSorting, 10) || null;
            if (sampleRowSorting && (!existingRow.sampleRowSorting || sampleRowSorting < existingRow.sampleRowSorting)) {
                existingRow.sampleRowSorting = sampleRowSorting;
            }
        });

        const tableRows = Array.from(tableRowsByKey.values()).sort((a, b) => {
            const rowSortingDiff = (a.sampleRowSorting || Number.MAX_SAFE_INTEGER) -
                (b.sampleRowSorting || Number.MAX_SAFE_INTEGER);
            if (rowSortingDiff !== 0) return rowSortingDiff;

            const aName = (a.colorName || '') + '-' + (a.yarnName || '');
            const bName = (b.colorName || '') + '-' + (b.yarnName || '');
            return aName.localeCompare(bName, 'tr');
        });

        return { tableRows, columnCount };
    };

    // Initialize form with initialData for edit mode
    useEffect(() => {
        if (initialData) {
            const parseDecimal = (value) => {
                if (!value && value !== 0) return { whole: '', decimal: '' };
                const num = (parseFloat(value) / 100).toFixed(2);
                const [whole, decimal] = num.split('.');
                return { whole, decimal };
            };
            const fabricWeight = initialData.FabricWeight || '';
            const price = parseDecimal(initialData.Price);
            const warpDensity = parseDecimal(initialData.WarpDensity);
            const weftDensity = parseDecimal(initialData.WeftDensity);
            const grayWarp = parseDecimal(initialData.GrayWarp);
            const grayWeft = parseDecimal(initialData.GrayWeft);

            let dateValue = '';
            if (initialData.Datetime) {
                try {
                    const dateStr = initialData.Datetime.toString().padStart(8, '0');
                    if (dateStr.length === 8) {
                        const first4 = parseInt(dateStr.substring(0, 4), 10);
                        if (first4 > 1231) {
                            const year = dateStr.substring(0, 4);
                            const month = dateStr.substring(4, 6);
                            const day = dateStr.substring(6, 8);
                            dateValue = `${year}-${month}-${day}`;
                        } else {
                            const day = dateStr.substring(0, 2);
                            const month = dateStr.substring(2, 4);
                            const year = dateStr.substring(4, 8);
                            dateValue = `${year}-${month}-${day}`;
                        }
                    }
                } catch (err) {
                    console.error('Date parse error:', err);
                }
            }

            setFormData({
                CardNo: initialData.CardNo || '',
                CompanyName: initialData.CompanyName || '',
                Note: initialData.Note || '',
                Certificate: initialData.Certificate || '',
                FabricWidth: initialData.FabricWidth || '',
                FabricWeight: fabricWeight,
                ReedWidth: initialData.ReedWidth || '',
                PriceWhole: price.whole,
                PriceDecimal: price.decimal,
                Datetime: dateValue,
                CompositionGuid: initialData.CompositionGuid || initialData.PrdCompositionGuid || '',
                WeaveTypeGuid: initialData.WeaveTypeGuid || initialData.PrdWeaveTypeGuid || '',
                CurrencyGuid: initialData.CurrencyGuid || initialData.PrdCurrencyGuid || '',
                Photo: initialData.Photo || '',
                TotalWireCount: initialData.TotalThread || initialData.TotalWireCount || '',
                ReedNo: initialData.ReedNo || '',
                Dgts: initialData.Dgts || '',
                WarpDensityWhole: warpDensity.whole,
                WarpDensityDecimal: warpDensity.decimal,
                WeftDensityWhole: weftDensity.whole,
                WeftDensityDecimal: weftDensity.decimal,
                GrayWarpWhole: grayWarp.whole,
                GrayWarpDecimal: grayWarp.decimal,
                GrayWeftWhole: grayWeft.whole,
                GrayWeftDecimal: grayWeft.decimal
            });

            if (initialData.Photo) {
                setImagePreview(initialData.Photo);
            }

            // Load SampleColor data for editing
            const sampleGuid = initialData.SampleId || initialData.PrdSampleGuid || initialData.SampleGuid;
            console.log('Loading SampleColors for GUID:', sampleGuid);
            if (sampleGuid) {
                loadSampleColors(sampleGuid);
                loadSampleFactorsForReport(sampleGuid);
                setShowColorTable(true);
            }
        }
    }, [initialData]);

    useEffect(() => {
        const reedNo = parseFloat(formData.ReedNo) || 0;
        const dgts = parseFloat(formData.Dgts) || 0;
        const reedWidth = parseFloat(formData.ReedWidth) || 0;

        if (reedNo > 0 && dgts > 0 && reedWidth > 0) {
            const calculated = (reedNo * dgts * reedWidth / 10).toString();
            setFormData(prev => ({ ...prev, TotalWireCount: calculated }));
        } else {
            setFormData(prev => ({ ...prev, TotalWireCount: '' }));
        }
    }, [formData.ReedNo, formData.Dgts, formData.ReedWidth]);

    useEffect(() => {
        const totalWireCount = parseFloat(formData.TotalWireCount) || 0;
        const fabricWidth = parseFloat(formData.FabricWidth) || 0;

        if (totalWireCount > 0 && fabricWidth > 0) {
            const [whole, decimal] = (totalWireCount / fabricWidth).toFixed(2).split('.');
            setFormData(prev => ({
                ...prev,
                WarpDensityWhole: whole,
                WarpDensityDecimal: decimal
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                WarpDensityWhole: '',
                WarpDensityDecimal: ''
            }));
        }
    }, [formData.TotalWireCount, formData.FabricWidth]);

    // Show color table when CardNo is entered
    useEffect(() => {
        if (formData.CardNo.trim()) {
            setShowColorTable(true);
            if (warpTableData.length === 0 && weftTableData.length === 0) {
                initializeTables();
            }
        }
    }, [formData.CardNo]);

    const initializeTables = () => {
        const initialWarpRow = {
            id: Date.now() + 1,
            colorId: '',
            yarnId: '',
            colorName: '',
            colorCode: '',
            yarnName: '',
            weight: '',
            values: new Array(warpTableColumns).fill('')
        };

        const initialWeftRow = {
            id: Date.now() + 2,
            colorId: '',
            yarnId: '',
            colorName: '',
            colorCode: '',
            yarnName: '',
            weight: '',
            values: new Array(weftTableColumns).fill('')
        };

        setWarpTableData([initialWarpRow]);
        setWeftTableData([initialWeftRow]);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleDecimalInput = (e, field) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 2);
        handleInputChange(field, value);
    };

    const handleWholeInput = (e, field) => {
        const value = e.target.value.replace(/\D/g, '');
        handleInputChange(field, value);
    };

    const handlePriceKeyDown = (e, decimalField) => {
        const isDotOrComma = e.key === '.' || e.key === ',' ||
            e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
        if (isDotOrComma) {
            e.preventDefault();
            const decimalInput = document.querySelector(`input[name="${decimalField}"]`);
            if (decimalInput) {
                decimalInput.focus();
                decimalInput.select();
            }
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Lutfen bir gorsel dosyasi secin');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setCropImageSrc(reader.result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCropModalVisible(true);
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = (croppedArea, croppedAreaPx) => {
        setCroppedAreaPixels(croppedAreaPx);
    };

    const getCroppedImg = (imageSrc, pixelCrop) => {
        return new Promise((resolve) => {
            const image = new Image();
            image.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 350;
                canvas.height = 200;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(
                    image,
                    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                    0, 0, 350, 200
                );
                resolve(canvas.toDataURL('image/jpeg', 0.92));
            };
            image.src = imageSrc;
        });
    };

    const handleCropConfirm = async () => {
        if (!cropImageSrc || !croppedAreaPixels) return;
        const croppedImage = await getCroppedImg(cropImageSrc, croppedAreaPixels);
        setFormData(prev => ({ ...prev, Photo: croppedImage }));
        setImagePreview(croppedImage);
        setCropModalVisible(false);
        setCropImageSrc(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCropCancel = () => {
        setCropModalVisible(false);
        setCropImageSrc(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveImage = () => {
        setFormData(prev => ({ ...prev, Photo: '' }));
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Table handlers
    const handleColorSelection = (rowIndex, colorId, isWarp) => {
        const selectedColor = dropdownColors.find(c => c.ColorId === colorId) ||
            colorList.find(c => c.ColorId === colorId);
        if (selectedColor) {
            if (isWarp) {
                setWarpTableData(prev => prev.map((row, index) =>
                    index === rowIndex
                        ? { ...row, colorId, colorName: selectedColor.ColorName, colorCode: selectedColor.ColorCode }
                        : row
                ));
            } else {
                setWeftTableData(prev => prev.map((row, index) =>
                    index === rowIndex
                        ? { ...row, colorId, colorName: selectedColor.ColorName, colorCode: selectedColor.ColorCode }
                        : row
                ));
            }
        }
    };

    const handleYarnSelection = (rowIndex, yarnId, isWarp) => {
        const yarnInfo = dropdownYarns.find(y => y.Guid === yarnId);
        if (yarnInfo) {
            if (isWarp) {
                setWarpTableData(prev => prev.map((row, index) =>
                    index === rowIndex
                        ? { ...row, yarnId, yarnName: yarnInfo.Name }
                        : row
                ));
            } else {
                setWeftTableData(prev => prev.map((row, index) =>
                    index === rowIndex
                        ? { ...row, yarnId, yarnName: yarnInfo.Name }
                        : row
                ));
            }
        }
    };

    const normalizeThreadCountValue = (rawValue) => {
        if (rawValue === null || rawValue === undefined) return '';

        const digitsOnly = String(rawValue).replace(/[^0-9]/g, '');
        if (!digitsOnly) return '';

        const numericValue = parseInt(digitsOnly, 10);
        if (!Number.isFinite(numericValue) || numericValue <= 0) return '';

        return String(numericValue);
    };

    const hasValueInOtherRowForColumn = (rows, currentRowIndex, colIndex) => {
        return rows.some((row, rowIndex) => {
            if (rowIndex === currentRowIndex) return false;
            return (parseInt(row?.values?.[colIndex], 10) || 0) > 0;
        });
    };

    const handleCellValueChange = (rowIndex, colIndex, value, isWarp) => {
        const normalizedValue = normalizeThreadCountValue(value);

        if (isWarp) {
            setWarpTableData(prev => {
                if (normalizedValue !== '' && hasValueInOtherRowForColumn(prev, rowIndex, colIndex)) {
                    return prev;
                }

                return prev.map((row, index) => (
                    index === rowIndex
                        ? { ...row, values: row.values.map((v, i) => i === colIndex ? normalizedValue : v) }
                        : row
                ));
            });
        } else {
            setWeftTableData(prev => {
                if (normalizedValue !== '' && hasValueInOtherRowForColumn(prev, rowIndex, colIndex)) {
                    return prev;
                }

                return prev.map((row, index) => (
                    index === rowIndex
                        ? { ...row, values: row.values.map((v, i) => i === colIndex ? normalizedValue : v) }
                        : row
                ));
            });
        }
    };

    const getCellInputSelector = (tableType, rowIndex, colIndex) => (
        `input.table-input[data-table-type="${tableType}"][data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`
    );

    const focusNextAvailableCell = (tableType, startRow, startCol, rowStep, colStep, rowCount, colCount) => {
        let nextRow = startRow + rowStep;
        let nextCol = startCol + colStep;

        while (nextRow >= 0 && nextRow < rowCount && nextCol >= 0 && nextCol < colCount) {
            const input = document.querySelector(getCellInputSelector(tableType, nextRow, nextCol));

            if (input && !input.disabled) {
                input.focus();
                if (typeof input.select === 'function') input.select();
                return;
            }

            nextRow += rowStep;
            nextCol += colStep;
        }
    };

    const handleCellKeyDown = (event, rowIndex, colIndex, isWarp, rowCount, colCount) => {
        const tableType = isWarp ? 'warp' : 'weft';

        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                focusNextAvailableCell(tableType, rowIndex, colIndex, 0, 1, rowCount, colCount);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                focusNextAvailableCell(tableType, rowIndex, colIndex, 0, -1, rowCount, colCount);
                break;
            case 'ArrowDown':
                event.preventDefault();
                focusNextAvailableCell(tableType, rowIndex, colIndex, 1, 0, rowCount, colCount);
                break;
            case 'ArrowUp':
                event.preventDefault();
                focusNextAvailableCell(tableType, rowIndex, colIndex, -1, 0, rowCount, colCount);
                break;
            default:
                break;
        }
    };

    // Row/Column management
    const addRows = (isWarp, count = 1) => {
        const addCount = Math.max(1, Math.min(1000, parseInt(count, 10) || 1));
        const tableColumns = isWarp ? warpTableColumns : weftTableColumns;
        const newRows = [];

        for (let i = 0; i < addCount; i++) {
            newRows.push({
                id: Date.now() + Math.random() + i,
                colorId: '',
                yarnId: '',
                colorName: '',
                colorCode: '',
                yarnName: '',
                weight: '',
                values: new Array(tableColumns).fill('')
            });
        }

        if (isWarp) {
            setWarpTableData(prev => [...prev, ...newRows]);
        } else {
            setWeftTableData(prev => [...prev, ...newRows]);
        }
    };

    const removeRow = (rowIndex, isWarp) => {
        if (isWarp) {
            if (warpTableData.length > 1) {
                setWarpTableData(prev => prev.filter((_, index) => index !== rowIndex));
            }
        } else {
            if (weftTableData.length > 1) {
                setWeftTableData(prev => prev.filter((_, index) => index !== rowIndex));
            }
        }
    };

    const addColumns = (isWarp, count = 1) => {
        const addCount = Math.max(1, Math.min(1000, parseInt(count, 10) || 1));
        if (isWarp) {
            setWarpTableColumns(prev => prev + addCount);
        } else {
            setWeftTableColumns(prev => prev + addCount);
        }
    };

    const removeColumn = (isWarp) => {
        if (isWarp) {
            if (warpTableColumns > 1) {
                setWarpTableColumns(prev => prev - 1);
            }
        } else {
            if (weftTableColumns > 1) {
                setWeftTableColumns(prev => prev - 1);
            }
        }
    };

    const removeRows = (isWarp, count = 1) => {
        const removeCount = Math.max(1, parseInt(count) || 1);
        if (isWarp) {
            setWarpTableData(prev => {
                const newLength = Math.max(1, prev.length - removeCount);
                return prev.slice(0, newLength);
            });
        } else {
            setWeftTableData(prev => {
                const newLength = Math.max(1, prev.length - removeCount);
                return prev.slice(0, newLength);
            });
        }
    };

    const removeColumns = (isWarp, count = 1) => {
        const removeCount = Math.max(1, parseInt(count) || 1);
        if (isWarp) {
            setWarpTableColumns(prev => Math.max(1, prev - removeCount));
        } else {
            setWeftTableColumns(prev => Math.max(1, prev - removeCount));
        }
    };

    // Update columns for tables
    useEffect(() => {
        if (warpTableData.length > 0) {
            setWarpTableData(prev => prev.map(row => {
                if (row.values.length !== warpTableColumns) {
                    const newValues = [...row.values];
                    if (newValues.length < warpTableColumns) {
                        while (newValues.length < warpTableColumns) {
                            newValues.push('');
                        }
                    } else if (newValues.length > warpTableColumns) {
                        newValues.splice(warpTableColumns);
                    }
                    return { ...row, values: newValues };
                }
                return row;
            }));
        }
    }, [warpTableColumns]);

    useEffect(() => {
        if (weftTableData.length > 0) {
            setWeftTableData(prev => prev.map(row => {
                if (row.values.length !== weftTableColumns) {
                    const newValues = [...row.values];
                    if (newValues.length < weftTableColumns) {
                        while (newValues.length < weftTableColumns) {
                            newValues.push('');
                        }
                    } else if (newValues.length > weftTableColumns) {
                        newValues.splice(weftTableColumns);
                    }
                    return { ...row, values: newValues };
                }
                return row;
            }));
        }
    }, [weftTableColumns]);

    useEffect(() => {
        setWarpTableData(prev => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;

            const next = calculateTableWeightsForThreadType(prev, 2);
            const hasWeightChange = next.some((row, idx) => row.weight !== prev[idx]?.weight);

            return hasWeightChange ? next : prev;
        });
    }, [
        warpTableData,
        formData.ReedNo,
        formData.Dgts,
        formData.ReedWidth,
        dropdownYarns,
        loadedSampleFactorRowsByThreadType,
        pendingSampleFactorRowsByThreadType
    ]);

    useEffect(() => {
        setWeftTableData(prev => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;

            const next = calculateTableWeightsForThreadType(prev, 1);
            const hasWeightChange = next.some((row, idx) => row.weight !== prev[idx]?.weight);

            return hasWeightChange ? next : prev;
        });
    }, [
        weftTableData,
        formData.GrayWeftWhole,
        formData.GrayWeftDecimal,
        formData.ReedWidth,
        dropdownYarns,
        loadedSampleFactorRowsByThreadType,
        pendingSampleFactorRowsByThreadType
    ]);

    const handleBulkRowsChange = (value, isWarp) => {
        if (value === '') {
            setBulkAddRows(prev => ({ ...prev, [isWarp ? 'warp' : 'weft']: '' }));
            return;
        }
        const intVal = parseInt(value, 10);
        const finalVal = Number.isNaN(intVal) ? '' : Math.max(1, Math.min(1000, intVal));
        setBulkAddRows(prev => ({ ...prev, [isWarp ? 'warp' : 'weft']: finalVal }));
    };

    const handleBulkColumnsChange = (value, isWarp) => {
        if (value === '') {
            setBulkAddColumns(prev => ({ ...prev, [isWarp ? 'warp' : 'weft']: '' }));
            return;
        }
        const intVal = parseInt(value, 10);
        const finalVal = Number.isNaN(intVal) ? '' : Math.max(1, Math.min(1000, intVal));
        setBulkAddColumns(prev => ({ ...prev, [isWarp ? 'warp' : 'weft']: finalVal }));
    };

    const calculateRowTotal = (values, threadType) => {
        if (!Array.isArray(values) || values.length === 0) return 0;
        return values.reduce((sum, val, colIndex) => {
            const threadCount = parseInt(val, 10) || 0;
            const factor = getColumnFactorForThreadType(threadType, colIndex + 1);
            return sum + (threadCount * factor);
        }, 0);
    };

    const calculateRowPercentage = (rowTotal, grandTotal) => {
        return grandTotal > 0 ? ((rowTotal / grandTotal) * 100).toFixed(1) : '0.0';
    };

    const getGrandTotal = (tableData, threadType) => {
        return tableData.reduce((total, row) => total + calculateRowTotal(row.values, threadType), 0);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const toInt = (whole, decimal) => {
            const w = parseInt(whole || '0', 10);
            const d = parseInt((decimal || '00').padEnd(2, '0').slice(0, 2), 10);
            return (w * 100 + d).toString();
        };

        const totalWireCount = parseFloat(formData.TotalWireCount) || 0;
        const fabricWidth = parseFloat(formData.FabricWidth) || 0;
        const calculatedWarpDensity = totalWireCount > 0 && fabricWidth > 0
            ? Math.round((totalWireCount / fabricWidth) * 100).toString()
            : '0';

        let dateDecimal = '';
        if (formData.Datetime) {
            const dateParts = formData.Datetime.split('-');
            if (dateParts.length === 3) {
                dateDecimal = `${dateParts[2]}${dateParts[1]}${dateParts[0]}`;
            }
        }

        // Recalculate weights right before submit to avoid stale UI state.
        const weightedWarpTableData = calculateTableWeightsForThreadType(warpTableData, 2);
        const weightedWeftTableData = calculateTableWeightsForThreadType(weftTableData, 1);

        // Prepare table data with ThreadType
        const allTableData = [
            ...weightedWarpTableData.map(row => ({ ...row, threadType: 2 })),
            ...weightedWeftTableData.map(row => ({ ...row, threadType: 1 }))
        ];
        const buildSampleFactorRowsForSubmit = (threadType) => (
            getEffectiveSampleFactorRowsByThreadType(threadType)
                .map((row) => ({
                    sampleFactorId: row?.sampleFactorId || row?.SampleFactorId || '',
                    threadType: String(row?.threadType ?? row?.ThreadType ?? threadType),
                    firstColon: String(row?.firstColon ?? row?.FirstColon ?? '').trim(),
                    secondColon: String(row?.secondColon ?? row?.SecondColon ?? '').trim(),
                    factor: String(row?.factor ?? row?.Factor ?? '').trim()
                }))
                .filter((row) => row.firstColon && row.secondColon && row.factor)
        );
        const effectiveSampleFactorRowsByThreadType = {
            1: buildSampleFactorRowsForSubmit(1),
            2: buildSampleFactorRowsForSubmit(2)
        };

        const submitData = {
            CardNo: formData.CardNo || '',
            CompanyName: formData.CompanyName || '',
            Note: formData.Note || '',
            Certificate: formData.Certificate || '',
            FabricWidth: (formData.FabricWidth || '').toString(),
            FabricWeight: (formData.FabricWeight || '').toString(),
            ReedWidth: (formData.ReedWidth || '').toString(),
            Price: toInt(formData.PriceWhole, formData.PriceDecimal),
            Datetime: dateDecimal,
            CompositionGuid: formData.CompositionGuid || '',
            WeaveTypeGuid: formData.WeaveTypeGuid || '',
            CurrencyGuid: formData.CurrencyGuid || '',
            Photo: formData.Photo || '',
            TotalThread: (formData.TotalWireCount || '').toString(),
            ReedNo: (formData.ReedNo || '').toString(),
            Dgts: (formData.Dgts || '').toString(),
            WarpDensity: calculatedWarpDensity,
            WeftDensity: toInt(formData.WeftDensityWhole, formData.WeftDensityDecimal),
            GrayWarp: toInt(formData.GrayWarpWhole, formData.GrayWarpDecimal),
            GrayWeft: toInt(formData.GrayWeftWhole, formData.GrayWeftDecimal),
            // Include table data
            warpTableData: weightedWarpTableData,
            weftTableData: weightedWeftTableData,
            warpTableColumns,
            weftTableColumns,
            allTableData,
            sampleFactorRowsByThreadType: effectiveSampleFactorRowsByThreadType
        };

        console.log('SampleInputs - warpTableData:', weightedWarpTableData);
        console.log('SampleInputs - weftTableData:', weightedWeftTableData);
        console.log('SampleInputs - allTableData:', allTableData);
        console.log('SampleInputs - submitData:', submitData);

        onSubmit(submitData);
    };

    const renderTable = (isWarp) => {
        const tableData = isWarp ? warpTableData : weftTableData;
        const tableColumns = isWarp ? warpTableColumns : weftTableColumns;
        const threadType = isWarp ? 2 : 1;
        const grandTotal = getGrandTotal(tableData, threadType);
        const colorOptions = dropdownColors.length > 0 ? dropdownColors : colorList;
        const factorRows = getFactorRowsForThreadType(threadType, true);

        const getFactorRangeForColumn = (columnNo) => (
            factorRows.find(range => columnNo >= range.start && columnNo <= range.end) || null
        );

        const getWeightedCellValue = (row, colIndex) => {
            const threadCount = parseInt(row.values[colIndex], 10) || 0;
            const factor = getColumnFactorForThreadType(threadType, colIndex + 1);
            return threadCount * factor;
        };

        return (
            <div className="sample-table-container">
                <CTable responsive className="sample-color-table mb-0">
                    <CTableHead>
                        <CTableRow>
                            <CTableHeaderCell className="color-column">{t('colorName')}</CTableHeaderCell>
                            <CTableHeaderCell className="code-column">{t('colorNumber')}</CTableHeaderCell>
                            <CTableHeaderCell className="yarn-column">{t('yarnNumber')}</CTableHeaderCell>
                            {Array.from({ length: tableColumns }, (_, i) => {
                                const columnNo = i + 1;
                                const factorRange = getFactorRangeForColumn(columnNo);
                                return (
                                    <CTableHeaderCell
                                        key={i}
                                        className="number-column"
                                        style={factorRange ? { backgroundColor: factorRange.backgroundColor } : undefined}
                                    >
                                        {columnNo}
                                    </CTableHeaderCell>
                                );
                            })}
                            <CTableHeaderCell className="total-column">{t('total')}</CTableHeaderCell>
                            <CTableHeaderCell className="percent-column">%</CTableHeaderCell>
                            <CTableHeaderCell className="action-column">{t('action')}</CTableHeaderCell>
                        </CTableRow>
                    </CTableHead>
                    <CTableBody>
                        {tableData.map((row, rowIndex) => {
                            const rowTotal = getWeightedRowTotalForThreadType(row, threadType);
                            const rowPercentage = calculateRowPercentage(rowTotal, grandTotal);

                            return (
                                <CTableRow key={`${row.id}-${rowIndex}`}>
                                    <CTableDataCell className="color-column">
                                        <SearchableSelect
                                            options={colorOptions}
                                            value={row.colorId}
                                            onChange={(selectedValue) => handleColorSelection(rowIndex, selectedValue, isWarp)}
                                            placeholder={t('selectColor')}
                                            size="sm"
                                            displayKey="ColorName"
                                            valueKey="ColorId"
                                            emptyText={t('No colors found')}
                                        />
                                    </CTableDataCell>
                                    <CTableDataCell className="code-column">
                                        <span className="color-code">{row.colorCode || '-'}</span>
                                    </CTableDataCell>
                                    <CTableDataCell className="yarn-column">
                                        <SearchableSelect
                                            options={dropdownYarns}
                                            value={row.yarnId}
                                            onChange={(selectedValue) => handleYarnSelection(rowIndex, selectedValue, isWarp)}
                                            placeholder={t('selectYarn')}
                                            size="sm"
                                            displayKey="Name"
                                            valueKey="Guid"
                                            emptyText={t('No yarns found')}
                                        />
                                    </CTableDataCell>
                                    {row.values.map((value, colIndex) => {
                                        const hasOtherValueInColumn = hasValueInOtherRowForColumn(tableData, rowIndex, colIndex);
                                        const currentCellValue = parseInt(value, 10) || 0;
                                        const isLockedByColumnRule = hasOtherValueInColumn && currentCellValue <= 0;

                                        return (
                                            <CTableDataCell
                                                key={colIndex}
                                                className="number-column"
                                                style={getFactorRangeForColumn(colIndex + 1)
                                                    ? { backgroundColor: getFactorRangeForColumn(colIndex + 1).backgroundColor }
                                                    : undefined}
                                            >
                                                <CFormInput
                                                    type="number"
                                                    size="sm"
                                                    value={currentCellValue > 0 ? value : ''}
                                                    onChange={(e) => handleCellValueChange(rowIndex, colIndex, e.target.value, isWarp)}
                                                    onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex, isWarp, tableData.length, tableColumns)}
                                                    className="table-input"
                                                    data-table-type={isWarp ? 'warp' : 'weft'}
                                                    data-row-index={rowIndex}
                                                    data-col-index={colIndex}
                                                    min="0"
                                                    placeholder=""
                                                    disabled={isLockedByColumnRule}
                                                />
                                            </CTableDataCell>
                                        );
                                    })}
                                    <CTableDataCell className="total-column">
                                        <strong>{rowTotal > 0 ? rowTotal : ''}</strong>
                                    </CTableDataCell>
                                    <CTableDataCell className="percent-column">
                                        <strong className="percentage-value">{rowTotal > 0 && grandTotal > 0 ? `${rowPercentage}%` : ''}</strong>
                                    </CTableDataCell>
                                    <CTableDataCell className="action-column">
                                        <CButton
                                            size="sm"
                                            color="danger"
                                            variant="outline"
                                            onClick={() => removeRow(rowIndex, isWarp)}
                                            disabled={tableData.length <= 1}
                                            title={t('removeRow')}
                                        >
                                            <CIcon icon={cilMinus} size="sm" />
                                        </CButton>
                                    </CTableDataCell>
                                </CTableRow>
                            );
                        })}
                        <CTableRow className="total-row">
                            <CTableDataCell colSpan={3} className="text-end pe-3 sticky-summary-label">
                                <strong>{t('total')}:</strong>
                            </CTableDataCell>
                            {Array.from({ length: tableColumns }, (_, colIndex) => {
                                const factorRange = getFactorRangeForColumn(colIndex + 1);
                                const columnTotal = tableData.reduce(
                                    (sum, row) => sum + getWeightedCellValue(row, colIndex),
                                    0
                                );
                                return (
                                    <CTableDataCell
                                        key={colIndex}
                                        className="number-column"
                                        style={factorRange ? { backgroundColor: factorRange.backgroundColor } : undefined}
                                    >
                                        <strong>{columnTotal > 0 ? columnTotal : ''}</strong>
                                    </CTableDataCell>
                                );
                            })}
                            <CTableDataCell className="total-column total-cell">
                                <strong>{grandTotal > 0 ? grandTotal : ''}</strong>
                            </CTableDataCell>
                            <CTableDataCell className="percent-column total-cell">
                                <strong>{grandTotal > 0 ? '100.0%' : ''}</strong>
                            </CTableDataCell>
                            <CTableDataCell className="action-column"></CTableDataCell>
                        </CTableRow>

                        {factorRows.length > 0 && (
                            <CTableRow className="factor-row">
                                <CTableDataCell colSpan={3} className="text-end pe-3 sticky-summary-label">
                                    <strong>{t('factor') || 'Carpan'}</strong>
                                </CTableDataCell>
                                {Array.from({ length: tableColumns }, (_, colIndex) => {
                                    const columnNo = colIndex + 1;
                                    const factorRange = getFactorRangeForColumn(columnNo);
                                    const midpointColumn = factorRange
                                        ? Math.round((Number(factorRange.start) + Number(factorRange.end)) / 2)
                                        : -1;
                                    const label = factorRange && columnNo === midpointColumn
                                        ? `X${factorRange.factor}`
                                        : '';

                                    return (
                                        <CTableDataCell
                                            key={`factor-${columnNo}`}
                                            className="number-column text-center factor-cell"
                                            style={factorRange ? { backgroundColor: factorRange.backgroundColor } : undefined}
                                        >
                                            <strong>{label}</strong>
                                        </CTableDataCell>
                                    );
                                })}
                                <CTableDataCell className="total-column"></CTableDataCell>
                                <CTableDataCell className="percent-column"></CTableDataCell>
                                <CTableDataCell className="action-column"></CTableDataCell>
                            </CTableRow>
                        )}
                    </CTableBody>
                </CTable>
            </div>
        );
    };

    if (dropdownLoading) {
        return (
            <div className="text-center py-4">
                <CSpinner color="primary" />
                <p className="mt-2">{t('loading')}</p>
            </div>
        );
    }

    return (
        <CForm onSubmit={handleSubmit} className="sample-inputs-form">
            {error && (
                <CAlert color="danger" className="mb-3">
                    {error}
                </CAlert>
            )}

            {dropdownError && (
                <CAlert color="warning" className="mb-3">
                    {dropdownError}
                </CAlert>
            )}

            {quickCreateFeedback && (
                <CAlert
                    color={quickCreateFeedback.type || 'success'}
                    className="mb-3"
                    dismissible
                    onClose={() => setQuickCreateFeedback(null)}
                >
                    {quickCreateFeedback.message}
                </CAlert>
            )}

            {loadingSampleColors && (
                <CAlert color="info" className="d-flex align-items-center">
                    <CSpinner size="sm" className="me-2" />
                    {t('loading')}...
                </CAlert>
            )}

            <CRow className="mb-3">
                {/* Image Upload Section */}
                <CCol md={4}>
                    <div className="image-upload-section">
                        <CFormLabel>{t('sampleImage')}</CFormLabel>
                        <div className="image-upload-container" onClick={handleImageClick}>
                            {imagePreview ? (
                                <div className="image-preview-wrapper">
                                    <img src={imagePreview} alt="Preview"
                                        className="image-preview"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setImageModal(true);
                                        }} />
                                    <CButton
                                        color="danger"
                                        size="sm"
                                        className="remove-image-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveImage();
                                        }}
                                    >
                                        <CIcon icon={cilX} />
                                    </CButton>
                                </div>
                            ) : (
                                <div className="upload-placeholder">
                                    <CIcon icon={cilImage} size="3xl" />
                                    <span>{t('selectImage')}</span>
                                    <span className="upload-hint">350x200</span>
                                </div>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            style={{ display: 'none' }}
                        />
                    </div>
                </CCol>

                {/* Form Fields */}
                <CCol md={8}>
                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel>{t('cardNo')} *</CFormLabel>
                            <CFormInput
                                type="text"
                                value={formData.CardNo}
                                onChange={(e) => handleInputChange('CardNo', e.target.value)}
                                placeholder={t('cardNo')}
                            />
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel>{t('companyName')}</CFormLabel>
                            <CFormInput
                                type="text"
                                value={formData.CompanyName}
                                onChange={(e) => handleInputChange('CompanyName', e.target.value)}
                                placeholder={t('companyName')}
                            />
                        </CCol>
                    </CRow>

                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel>{t('certificate')}</CFormLabel>
                            <CFormInput
                                type="text"
                                value={formData.Certificate}
                                onChange={(e) => handleInputChange('Certificate', e.target.value)}
                                placeholder={t('certificate')}
                            />
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel>{t('date')}</CFormLabel>
                            <CFormInput
                                type="date"
                                value={formData.Datetime}
                                onChange={(e) => handleInputChange('Datetime', e.target.value)}
                            />
                        </CCol>
                    </CRow>

                    <CRow className="mb-3">
                        <CCol md={6}>
                            <CFormLabel>{t('composition')}</CFormLabel>
                            <CInputGroup className="quick-create-select-group">
                                <SearchableSelect
                                    id="CompositionGuid"
                                    name="CompositionGuid"
                                    options={compositionOptions.map(c => ({
                                        Guid: c.CompositionGuid || c.PrdCompositionGuid,
                                        Name: c.ThreadName || c.CompositionName || c.Name
                                    }))}
                                    value={formData.CompositionGuid}
                                    onChange={(selectedValue) => handleInputChange('CompositionGuid', selectedValue)}
                                    onSearchTermChange={(searchTerm) => handleDropdownSearch('CompositionGuid', searchTerm)}
                                    placeholder={t('select')}
                                    disabled={dropdownLoading}
                                    displayKey="Name"
                                    valueKey="Guid"
                                    emptyText={t('No options found')}
                                    className="quick-create-searchable-select"
                                />
                                <CButton
                                    type="button"
                                    color="success"
                                    variant="outline"
                                    onClick={handleOpenAddCompositionModal}
                                    disabled={dropdownLoading}
                                    title={t('createComposition') || 'Kompozisyon Ekle'}
                                >
                                    <CIcon icon={cilPlus} />
                                </CButton>
                            </CInputGroup>
                        </CCol>
                        <CCol md={6}>
                            <CFormLabel>{t('weaveType')}</CFormLabel>
                            <CInputGroup className="quick-create-select-group">
                                <SearchableSelect
                                    id="WeaveTypeGuid"
                                    name="WeaveTypeGuid"
                                    options={weaveTypeOptions.map(w => ({
                                        Guid: w.WeaveTypeGuid || w.PrdWeaveTypeGuid,
                                        Name: w.WeaveTypeName || w.Name
                                    }))}
                                    value={formData.WeaveTypeGuid}
                                    onChange={(selectedValue) => handleInputChange('WeaveTypeGuid', selectedValue)}
                                    onSearchTermChange={(searchTerm) => handleDropdownSearch('WeaveTypeGuid', searchTerm)}
                                    placeholder={t('select')}
                                    disabled={dropdownLoading}
                                    displayKey="Name"
                                    valueKey="Guid"
                                    emptyText={t('No options found')}
                                    className="quick-create-searchable-select"
                                />
                                <CButton
                                    type="button"
                                    color="success"
                                    variant="outline"
                                    onClick={handleOpenAddWeaveTypeModal}
                                    disabled={dropdownLoading}
                                    title={t('createWeaveType') || 'Orgu Tipi Ekle'}
                                >
                                    <CIcon icon={cilPlus} />
                                </CButton>
                            </CInputGroup>
                        </CCol>
                    </CRow>
                </CCol>
            </CRow>

            {/* Decimal Fields Row */}
            <CRow className="mb-3">
                <CCol md={3}>
                    <CFormLabel>{t('fabricWidth')} (cm)</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.FabricWidth}
                        onChange={(e) => handleWholeInput(e, 'FabricWidth')}
                        placeholder="0"
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('fabricWeight')} (gr/m2)</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.FabricWeight}
                        onChange={(e) => handleWholeInput(e, 'FabricWeight')}
                        placeholder="0"
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('price')}</CFormLabel>
                    <div className="decimal-input-group">
                        <CFormInput
                            type="text"
                            className="whole-input"
                            value={formData.PriceWhole}
                            onChange={(e) => handleWholeInput(e, 'PriceWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'PriceDecimal')}
                            placeholder="0"
                        />
                        <span className="decimal-separator">,</span>
                        <CFormInput
                            type="text"
                            name="PriceDecimal"
                            className="decimal-input"
                            value={formData.PriceDecimal}
                            onChange={(e) => handleDecimalInput(e, 'PriceDecimal')}
                            placeholder="00"
                        />
                    </div>
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('currency')}</CFormLabel>
                    <CFormSelect
                        value={formData.CurrencyGuid}
                        onChange={(e) => handleInputChange('CurrencyGuid', e.target.value)}
                    >
                        <option value="">{t('select')}</option>
                        {currencyOptions.map(c => (
                            <option key={c.CurrencyGuid || c.PrdCurrencyGuid}
                                value={c.CurrencyGuid || c.PrdCurrencyGuid}>
                                {c.CurrencyName || c.Name}
                            </option>
                        ))}
                    </CFormSelect>
                </CCol>
            </CRow>

            {/* New Technical Fields Row 1 */}
            <CRow className="mb-3">
                <CCol md={3}>
                    <CFormLabel>{t('totalWireCount')}</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.TotalWireCount}
                        placeholder="0"
                        disabled
                        readOnly
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('reedNo')}</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.ReedNo}
                        onChange={(e) => handleWholeInput(e, 'ReedNo')}
                        placeholder="0"
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('dgts')}</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.Dgts}
                        onChange={(e) => handleWholeInput(e, 'Dgts')}
                        placeholder="0"
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('reedWidth')} (cm)</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.ReedWidth}
                        onChange={(e) => handleWholeInput(e, 'ReedWidth')}
                        placeholder="0"
                    />
                </CCol>
            </CRow>

            {/* New Technical Fields Row 2 */}
            <CRow className="mb-3">
                <CCol md={3}>
                    <CFormLabel>{t('warpDensityi')}</CFormLabel>
                    <CFormInput
                        type="text"
                        value={formData.WarpDensityWhole
                            ? `${formData.WarpDensityWhole},${formData.WarpDensityDecimal || '00'}`
                            : ''}
                        placeholder="0,00"
                        disabled
                        readOnly
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                    />
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('weftDensityi')}</CFormLabel>
                    <div className="decimal-input-group">
                        <CFormInput
                            type="text"
                            className="whole-input"
                            value={formData.WeftDensityWhole}
                            onChange={(e) => handleWholeInput(e, 'WeftDensityWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'WeftDensityDecimal')}
                            placeholder="0"
                        />
                        <span className="decimal-separator">,</span>
                        <CFormInput
                            type="text"
                            name="WeftDensityDecimal"
                            className="decimal-input"
                            value={formData.WeftDensityDecimal}
                            onChange={(e) => handleDecimalInput(e, 'WeftDensityDecimal')}
                            placeholder="00"
                        />
                    </div>
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('grayWarp')}</CFormLabel>
                    <div className="decimal-input-group">
                        <CFormInput
                            type="text"
                            className="whole-input"
                            value={formData.GrayWarpWhole}
                            onChange={(e) => handleWholeInput(e, 'GrayWarpWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'GrayWarpDecimal')}
                            placeholder="0"
                        />
                        <span className="decimal-separator">,</span>
                        <CFormInput
                            type="text"
                            name="GrayWarpDecimal"
                            className="decimal-input"
                            value={formData.GrayWarpDecimal}
                            onChange={(e) => handleDecimalInput(e, 'GrayWarpDecimal')}
                            placeholder="00"
                        />
                    </div>
                </CCol>
                <CCol md={3}>
                    <CFormLabel>{t('grayWeft')}</CFormLabel>
                    <div className="decimal-input-group">
                        <CFormInput
                            type="text"
                            className="whole-input"
                            value={formData.GrayWeftWhole}
                            onChange={(e) => handleWholeInput(e, 'GrayWeftWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'GrayWeftDecimal')}
                            placeholder="0"
                        />
                        <span className="decimal-separator">,</span>
                        <CFormInput
                            type="text"
                            name="GrayWeftDecimal"
                            className="decimal-input"
                            value={formData.GrayWeftDecimal}
                            onChange={(e) => handleDecimalInput(e, 'GrayWeftDecimal')}
                            placeholder="00"
                        />
                    </div>
                </CCol>
            </CRow>

            {/* Note Field */}
            <CRow className="mb-4">
                <CCol md={12}>
                    <CFormLabel>{t('note')}</CFormLabel>
                    <CFormTextarea
                        rows={3}
                        value={formData.Note}
                        onChange={(e) => handleInputChange('Note', e.target.value)}
                        placeholder={t('note')}
                    />
                </CCol>
            </CRow>

            {/* Color Table Section - Shows after CardNo is entered */}
            {showColorTable && (
                <CCard className="sample-tabs-card mb-4 fade-in">
                    <div className="tabs-header">
                        <CNav variant="tabs" role="tablist" className="sample-tabs">
                            <CNavItem>
                                <CNavLink
                                    active={activeTab === 'cozgu'}
                                    onClick={() => setActiveTab('cozgu')}
                                    className={`sample-tab-link ${activeTab === 'cozgu' ? 'active' : ''}`}
                                >
                                    <CIcon icon={cilColorPalette} className="me-2" />
                                    {t('warp')}
                                </CNavLink>
                            </CNavItem>
                            <CNavItem>
                                <CNavLink
                                    active={activeTab === 'atki'}
                                    onClick={() => setActiveTab('atki')}
                                    className={`sample-tab-link ${activeTab === 'atki' ? 'active' : ''}`}
                                >
                                    <CIcon icon={cilGrid} className="me-2" />
                                    {t('weft')}
                                </CNavLink>
                            </CNavItem>
                        </CNav>

                        {/* Table Controls */}
                        <div className="table-controls">
                            <div className="bulk-controls-row">
                                {/* Row Controls */}
                                <div className="control-group">
                                    <label className="control-label">{t('rows')}:</label>
                                    <CInputGroup size="sm" className="bulk-input-group">
                                        <CButton
                                            size="sm"
                                            color="danger"
                                            variant="outline"
                                            className="left-btn"
                                            onClick={() => removeRows(activeTab === 'cozgu', activeTab === 'cozgu' ? bulkAddRows.warp : bulkAddRows.weft)}
                                            disabled={(activeTab === 'cozgu' ? warpTableData.length : weftTableData.length) <= 1}
                                        >
                                            <CIcon icon={cilMinus} size="sm" />
                                        </CButton>
                                        <CFormInput
                                            type="number"
                                            min="1"
                                            max="1000"
                                            value={activeTab === 'cozgu' ? bulkAddRows.warp : bulkAddRows.weft}
                                            onChange={(e) => handleBulkRowsChange(e.target.value, activeTab === 'cozgu')}
                                            className="bulk-number-input has-left-button"
                                            placeholder="1"
                                        />
                                        <CButton
                                            size="sm"
                                            color="success"
                                            variant="outline"
                                            className="right-btn"
                                            onClick={() => addRows(activeTab === 'cozgu', activeTab === 'cozgu' ? bulkAddRows.warp : bulkAddRows.weft)}
                                        >
                                            <CIcon icon={cilPlus} size="sm" />
                                        </CButton>
                                    </CInputGroup>
                                </div>

                                {/* Column Controls */}
                                <div className="control-group">
                                    <label className="control-label">{t('columns')}:</label>
                                    <CInputGroup size="sm" className="bulk-input-group">
                                        <CButton
                                            size="sm"
                                            color="danger"
                                            variant="outline"
                                            className="left-btn"
                                            onClick={() => removeColumns(activeTab === 'cozgu', activeTab === 'cozgu' ? bulkAddColumns.warp : bulkAddColumns.weft)}
                                            disabled={(activeTab === 'cozgu' ? warpTableColumns : weftTableColumns) <= 1}
                                        >
                                            <CIcon icon={cilMinus} size="sm" />
                                        </CButton>
                                        <CFormInput
                                            type="number"
                                            min="1"
                                            max="1000"
                                            value={activeTab === 'cozgu' ? bulkAddColumns.warp : bulkAddColumns.weft}
                                            onChange={(e) => handleBulkColumnsChange(e.target.value, activeTab === 'cozgu')}
                                            className="bulk-number-input has-left-button"
                                            placeholder="1"
                                        />
                                        <CButton
                                            size="sm"
                                            color="success"
                                            variant="outline"
                                            className="right-btn"
                                            onClick={() => addColumns(activeTab === 'cozgu', activeTab === 'cozgu' ? bulkAddColumns.warp : bulkAddColumns.weft)}
                                        >
                                            <CIcon icon={cilPlus} size="sm" />
                                        </CButton>
                                    </CInputGroup>
                                </div>

                                {/* Remove Column Button */}
                                <div className="control-group">
                                    <CButton
                                        size="sm"
                                        color="danger"
                                        variant="outline"
                                        onClick={() => removeColumn(activeTab === 'cozgu')}
                                        disabled={activeTab === 'cozgu' ? warpTableColumns <= 1 : weftTableColumns <= 1}
                                    >
                                        <CIcon icon={cilMinus} size="sm" /> {t('removeColumn')}
                                    </CButton>
                                </div>

                                {/* SampleFactor Button */}
                                <div className="control-group">
                                    <CButton
                                        size="sm"
                                        color="primary"
                                        variant="outline"
                                        onClick={handleOpenSampleFactorModal}
                                        title={t('addFactor') || 'Carpan Ekle'}
                                    >
                                        <CIcon icon={cilPlus} size="sm" /> {t('addFactor') || 'Carpan Ekle'}
                                    </CButton>
                                </div>
                            </div>
                        </div>
                    </div>

                    <CCardBody className="p-0">
                        <CTabContent>
                            <CTabPane visible={activeTab === 'cozgu'}>
                                {renderTable(true)}
                            </CTabPane>
                            <CTabPane visible={activeTab === 'atki'}>
                                {renderTable(false)}
                            </CTabPane>
                        </CTabContent>
                    </CCardBody>
                </CCard>
            )}

            {/* Action Buttons - Styled like WeaveInputs */}
            <CRow className="mt-4">
                <CCol className="d-flex justify-content-start gap-2">
                    <CButton
                        color="primary"
                        type="submit"
                        disabled={isLoading}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}
                    >
                        {isLoading ? (
                            <CSpinner size="sm" className="me-1" />
                        ) : (
                            <CIcon icon={cilSave} className="me-1" />
                        )}
                        {t('save')}
                    </CButton>
                    <CButton
                        color="secondary"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isLoading}
                        style={{
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1.5rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            border: '2px solid #6c757d'
                        }}
                    >
                        <CIcon icon={cilX} className="me-1" />
                        {t('cancel')}
                    </CButton>
                </CCol>
            </CRow>

            <CModal visible={showSampleFactorModal} onClose={handleCloseSampleFactorModal} size="lg">
                <CModalHeader>
                    <CModalTitle>{t('addFactor') || 'Carpan Ekle'}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <span className="me-2">{t('threadType') || 'Iplik Tipi'}:</span>
                            <CBadge color="primary">{getCurrentThreadType() === 2 ? (t('warp') || 'Cozgu') : (t('weft') || 'Atki')}</CBadge>
                        </div>
                        <CButton color="success" variant="outline" size="sm" onClick={handleAddSampleFactorRow}>
                            <CIcon icon={cilPlus} className="me-1" /> {t('addRow') || 'Satir Ekle'}
                        </CButton>
                    </div>

                    {sampleFactorModalError && <CAlert color="danger">{sampleFactorModalError}</CAlert>}

                    {sampleFactorModalLoading ? (
                        <div className="d-flex align-items-center">
                            <CSpinner size="sm" className="me-2" />
                            <span>{t('loading') || 'Yukleniyor...'}</span>
                        </div>
                    ) : (
                        <CTable bordered responsive>
                            <CTableHead>
                                <CTableRow>
                                    <CTableHeaderCell>{t('firstColumn') || 'Ilk Sutun'}</CTableHeaderCell>
                                    <CTableHeaderCell>{t('secondColumn') || 'Ikinci Sutun'}</CTableHeaderCell>
                                    <CTableHeaderCell>{t('factor') || 'Carpan'}</CTableHeaderCell>
                                    <CTableHeaderCell style={{ width: '90px' }}>{t('action') || 'Islem'}</CTableHeaderCell>
                                </CTableRow>
                            </CTableHead>
                            <CTableBody>
                                {sampleFactorRows.map((row) => (
                                    <CTableRow key={row.localId}>
                                        <CTableDataCell>
                                            <CFormInput
                                                value={row.firstColon}
                                                onChange={(e) => handleSampleFactorInputChange(row.localId, 'firstColon', e.target.value)}
                                                placeholder="0"
                                            />
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CFormInput
                                                value={row.secondColon}
                                                onChange={(e) => handleSampleFactorInputChange(row.localId, 'secondColon', e.target.value)}
                                                placeholder="0"
                                            />
                                        </CTableDataCell>
                                        <CTableDataCell>
                                            <CFormInput
                                                value={row.factor}
                                                onChange={(e) => handleSampleFactorInputChange(row.localId, 'factor', e.target.value)}
                                                placeholder="0"
                                            />
                                        </CTableDataCell>
                                        <CTableDataCell className="text-center align-middle">
                                            <CButton
                                                color="danger"
                                                variant="outline"
                                                size="sm"
                                                className="d-inline-flex align-items-center justify-content-center p-0"
                                                style={{ width: '28px', height: '28px' }}
                                                onClick={() => handleRemoveSampleFactorRow(row.localId)}
                                            >
                                                <CIcon icon={cilMinus} size="sm" />
                                            </CButton>
                                        </CTableDataCell>
                                    </CTableRow>
                                ))}
                            </CTableBody>
                        </CTable>
                    )}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" variant="outline" onClick={handleCloseSampleFactorModal}>
                        <CIcon icon={cilX} className="me-1" /> {t('cancel') || 'Iptal'}
                    </CButton>
                    <CButton color="primary" onClick={handleSaveSampleFactorModal}>
                        <CIcon icon={cilSave} className="me-1" /> {t('save') || 'Kaydet'}
                    </CButton>
                </CModalFooter>
            </CModal>

            <CModal
                visible={showAddCompositionModal}
                onClose={() => {
                    setShowAddCompositionModal(false);
                    resetAddCompositionModal();
                }}
                size="lg"
            >
                <CModalHeader>
                    <CModalTitle>{t('createComposition') || 'Kompozisyon Ekle'}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {addCompositionErrors.general && <CAlert color="danger">{addCompositionErrors.general}</CAlert>}
                    <CRow className="g-3">
                        <CCol md={12}>
                            <CFormLabel>{t('threadCompositionName') || 'Kompozisyon Adi'} *</CFormLabel>
                            <CFormInput
                                value={addCompositionForm.threadName}
                                onChange={(e) => {
                                    setAddCompositionForm(prev => ({ ...prev, threadName: e.target.value }));
                                    if (addCompositionErrors.threadName) {
                                        setAddCompositionErrors(prev => ({ ...prev, threadName: '' }));
                                    }
                                }}
                                invalid={!!addCompositionErrors.threadName}
                                disabled={addCompositionLoading}
                            />
                            {addCompositionErrors.threadName && (
                                <div className="invalid-feedback d-block">{addCompositionErrors.threadName}</div>
                            )}
                        </CCol>
                        <CCol md={12}>
                            <CFormLabel>{t('threadCompositionDescription') || 'Aciklama'}</CFormLabel>
                            <CFormTextarea
                                rows={3}
                                value={addCompositionForm.threadDescription}
                                onChange={(e) => setAddCompositionForm(prev => ({
                                    ...prev,
                                    threadDescription: e.target.value
                                }))}
                                disabled={addCompositionLoading}
                            />
                        </CCol>
                    </CRow>
                </CModalBody>
                <CModalFooter>
                    <CButton
                        type="button"
                        color="secondary"
                        variant="outline"
                        onClick={() => {
                            setShowAddCompositionModal(false);
                            resetAddCompositionModal();
                        }}
                        disabled={addCompositionLoading}
                    >
                        <CIcon icon={cilX} className="me-1" /> {t('cancel') || 'Iptal'}
                    </CButton>
                    <CButton
                        type="button"
                        color="primary"
                        onClick={handleAddCompositionSubmit}
                        disabled={addCompositionLoading}
                    >
                        {addCompositionLoading
                            ? <><CSpinner size="sm" className="me-1" />{t('saving') || 'Kaydediliyor...'}</>
                            : <><CIcon icon={cilSave} className="me-1" /> {t('save') || 'Kaydet'}</>}
                    </CButton>
                </CModalFooter>
            </CModal>

            <CModal
                visible={showAddWeaveTypeModal}
                onClose={() => {
                    setShowAddWeaveTypeModal(false);
                    resetAddWeaveTypeModal();
                }}
                size="lg"
            >
                <CModalHeader>
                    <CModalTitle>{t('createWeaveType') || 'Orgu Tipi Ekle'}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {addWeaveTypeErrors.general && <CAlert color="danger">{addWeaveTypeErrors.general}</CAlert>}
                    <CRow className="g-3">
                        <CCol md={12}>
                            <CFormLabel>{t('weaveTypeName') || 'Orgu Tipi Adi'} *</CFormLabel>
                            <CFormInput
                                value={addWeaveTypeForm.weaveTypeName}
                                onChange={(e) => {
                                    setAddWeaveTypeForm(prev => ({ ...prev, weaveTypeName: e.target.value }));
                                    if (addWeaveTypeErrors.weaveTypeName) {
                                        setAddWeaveTypeErrors(prev => ({ ...prev, weaveTypeName: '' }));
                                    }
                                }}
                                invalid={!!addWeaveTypeErrors.weaveTypeName}
                                disabled={addWeaveTypeLoading}
                            />
                            {addWeaveTypeErrors.weaveTypeName && (
                                <div className="invalid-feedback d-block">{addWeaveTypeErrors.weaveTypeName}</div>
                            )}
                        </CCol>
                        <CCol md={12}>
                            <CFormLabel>{t('Description') || 'Aciklama'}</CFormLabel>
                            <CFormTextarea
                                rows={3}
                                value={addWeaveTypeForm.weaveTypeDescription}
                                onChange={(e) => setAddWeaveTypeForm(prev => ({
                                    ...prev,
                                    weaveTypeDescription: e.target.value
                                }))}
                                disabled={addWeaveTypeLoading}
                            />
                        </CCol>
                    </CRow>
                </CModalBody>
                <CModalFooter>
                    <CButton
                        type="button"
                        color="secondary"
                        variant="outline"
                        onClick={() => {
                            setShowAddWeaveTypeModal(false);
                            resetAddWeaveTypeModal();
                        }}
                        disabled={addWeaveTypeLoading}
                    >
                        <CIcon icon={cilX} className="me-1" /> {t('cancel') || 'Iptal'}
                    </CButton>
                    <CButton type="button" color="primary" onClick={handleAddWeaveTypeSubmit} disabled={addWeaveTypeLoading}>
                        {addWeaveTypeLoading
                            ? <><CSpinner size="sm" className="me-1" />{t('saving') || 'Kaydediliyor...'}</>
                            : <><CIcon icon={cilSave} className="me-1" /> {t('save') || 'Kaydet'}</>}
                    </CButton>
                </CModalFooter>
            </CModal>

            {/* Image Preview Modal */}
            <CModal
                visible={imageModal}
                onClose={() => setImageModal(false)}
                size="lg"
                alignment="center"
                className="image-preview-modal"
            >
                <CModalHeader closeButton>
                    <CModalTitle>{t('sampleImage')}</CModalTitle>
                </CModalHeader>
                <CModalBody className="text-center p-0">
                    {imagePreview && (
                        <img
                            src={imagePreview}
                            alt="Sample"
                            className="preview-image"
                        />
                    )}
                </CModalBody>
            </CModal>

            {/* Image Crop Modal */}
            <CModal
                visible={cropModalVisible}
                onClose={handleCropCancel}
                size="lg"
                alignment="center"
                backdrop="static"
                className="crop-modal"
            >
                <CModalHeader closeButton>
                    <CModalTitle>Gorseli Kirp (350x200)</CModalTitle>
                </CModalHeader>
                <CModalBody className="p-0">
                    <div className="crop-container">
                        {cropImageSrc && (
                            <Cropper
                                image={cropImageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={350 / 200}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                showGrid={true}
                            />
                        )}
                    </div>
                    <div className="crop-controls">
                        <div className="zoom-control">
                            <label>Yakınlaştır:</label>
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.1}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                                className="zoom-slider"
                            />
                        </div>
                        <div className="crop-actions">
                            <CButton color="secondary" onClick={handleCropCancel}>
                                İptal
                            </CButton>
                            <CButton color="primary" onClick={handleCropConfirm}>
                                <CIcon icon={cilSave} className="me-1" />
                                Kırp ve Kaydet
                            </CButton>
                        </div>
                    </div>
                </CModalBody>
            </CModal>
        </CForm>
    );
};

export default SampleInputs;

